import type { FetchArgs, FetchBaseQueryError, FetchBaseQueryMeta, QueryReturnValue } from "@reduxjs/toolkit/query";
import PaginatedResponse, { Paginated } from "_shared/_api/pagination/responses/paginated-response";
import { api } from "_shared/_stores/api";
import type Guid from "_shared/guids/guid";
import { permissionQueryTags } from "permissions/_api/permission-cache-tags";
import BulkPermissionCommand from "permissions/_api/request-contracts/bulk-permission-command";
import PermissionCommand from "permissions/_api/request-contracts/permission-command";
import PermissionQuery, { PermissionView } from "permissions/_api/queries/permission-query";
import PermissionSchema, { PermissionJSON } from "permissions/_schemas/permission-schema";
import PermissionsByGranteeSchema, {
  PermissionsByGranteeJSON,
} from "permissions/by-grantee/_schemas/permissions-by-grantee-schema";
import Permission from "permissions/permission";
import type { Resource } from "permissions/resources/permission-resource";
import { z } from "zod";

type PermissionQueryArgs = {
  query: PermissionQuery;
};

type SessionPermissionQueryArgs = {
  query?: PermissionQuery;
};

type PermissionMutationArgs = {
  permission: Permission;
};

type ApplyTemplateMutationArgs = {
  templateId: Guid;
  /** The version the resources were read from; the server refuses the apply if the template changed since. */
  templateVersion: number;
  granteeUserId: Guid;
  /** The template's resources, as adjusted in the dialog for this one application. */
  resources: Resource[];
};

type BulkPermissionMutationArgs = {
  permissions: Permission[];
  replaceMatchingResourceTypes?: boolean;
};

const RestoredPermissionJSONZ = PermissionSchema.PermissionJSONZ.extend({
  supersededPermissionId: z.uuid().nullish(),
});

/**
 * The answer to `POST /permissions/{id}/re-grant`: the new grant, the id of the one it replaced, and
 * the derived grants the removal had withdrawn that came back with it, each with the id of the
 * withdrawn row it replaces (#6189).
 *
 * `restoredPermissions` is `undefined` when the field is missing altogether: a server from before
 * #6189 restores nothing and says nothing, and the page must not read that as "nothing to restore".
 * The API and the browser bundle are deployed by separate jobs, so for a while — a rolling deploy, a
 * rolled-back API — this bundle can talk to such a server. An empty list is a server that restores
 * and had nothing to restore.
 */
const RegrantedPermissionJSONZ = RestoredPermissionJSONZ.extend({
  restoredPermissions: z
    .array(RestoredPermissionJSONZ)
    .nullish()
    .transform((value) => value ?? undefined),
});

export type RegrantedPermissionJSON = z.infer<typeof RegrantedPermissionJSONZ>;

/**
 * The answer to `GET /permissions/{id}/lineage`: the grant, and every grant it replaced, newest first
 * (#6825). Each replaced row says who granted it (the empty id for the system) and who removed it
 * (null for the system).
 */
const PermissionLineageJSONZ = z.object({
  permission: PermissionSchema.PermissionJSONZ,
  replaced: z.array(PermissionSchema.PermissionJSONZ),
});

export type PermissionLineageJSON = z.infer<typeof PermissionLineageJSONZ>;

/**
 * The answer to `DELETE /permissions/{id}`: the disabled grant, and every derived grant the server
 * withdrew because the disabled one was all that justified it. An older server sends the grant alone,
 * which reads as nothing withdrawn.
 */
const DisabledPermissionJSONZ = PermissionSchema.PermissionJSONZ.extend({
  withdrawnPermissions: z.array(PermissionSchema.PermissionJSONZ).nullish().transform((value) => value ?? []),
});

export type DisabledPermissionJSON = z.infer<typeof DisabledPermissionJSONZ>;

/**
 * The answer to `POST /permissions/bulk`, which is what applying a template sends: the grants it
 * added, the ones it raised in place, the ones it disabled — a template replaces every grant of the
 * types it names — and the derived grants withdrawn behind those (#6175). It was one array of the
 * granted and raised rows until then.
 */
const BulkPermissionChangesJSONZ = z.object({
  granted: z.array(PermissionSchema.PermissionJSONZ),
  raised: z.array(PermissionSchema.PermissionJSONZ),
  disabled: z.array(PermissionSchema.PermissionJSONZ),
  withdrawn: z.array(PermissionSchema.PermissionJSONZ),
});

export type BulkPermissionChangesJSON = z.infer<typeof BulkPermissionChangesJSONZ>;

/**
 * The answer to `POST /permissions/templates/{templateId}/applications`: the application the server
 * recorded, and what it changed, as a bulk grant reports it (#6828).
 */
const TemplateApplicationJSONZ = BulkPermissionChangesJSONZ.extend({
  id: z.uuid(),
  templateId: z.uuid(),
  templateVersion: z.number().int().nonnegative(),
  granteeUserId: z.uuid(),
  appliedByUserId: z.uuid(),
  appliedDate: z.iso.datetime({ offset: true }),
});

export type TemplateApplicationJSON = z.infer<typeof TemplateApplicationJSONZ>;

/**
 * The notifications grant "Remove all access…" lowered to the person's own notifications: the row as
 * it left it, whose version the Undo sends, and the scope and access level it had, which the Undo
 * raises it back to.
 */
const LoweredNotificationsJSONZ = z.object({
  permission: PermissionSchema.PermissionJSONZ,
  previousScope: z.number(),
  previousAccessLevel: z.number(),
});

export type LoweredNotificationsJSON = z.infer<typeof LoweredNotificationsJSONZ>;

/**
 * The answer to `POST /permissions/disable-by-grantee`, "Remove all access…": every grant the call
 * disabled, the derived grants withdrawn behind them that it did not disable itself, and the
 * organization-wide notifications grant it lowered, or null (#6176).
 */
const GranteeAccessRemovalJSONZ = z.object({
  disabled: z.array(PermissionSchema.PermissionJSONZ),
  withdrawn: z.array(PermissionSchema.PermissionJSONZ),
  loweredNotifications: LoweredNotificationsJSONZ.nullish(),
});

export type GranteeAccessRemovalJSON = z.infer<typeof GranteeAccessRemovalJSONZ>;

type DisableGranteePermissionsArgs = {
  granteeUserId: Guid;
  /** "Also stop sign-in": the person's own sign-in goes too. */
  stopSignIn: boolean;
};

type RegrantPermissionsArgs = {
  /** The disabled grants to grant again, each with the version the removal left it at. */
  permissions: readonly PermissionJSON[];
  /** The notifications grant the removal lowered, raised back in place to the reach it had. */
  notifications?: LoweredNotificationsJSON | null;
};

const jsonPatchHeaders = {
  "content-type": "application/json-patch+json",
};

export const permissionListTag = { type: "Permission" as const, id: "LIST" };
export const sessionPermissionTag = { type: "Permission" as const, id: "SESSION" };
const getPermissionMutationTags = () => [
  permissionListTag,
  sessionPermissionTag,
  // A field grant can change the authorized proposal envelope without appending a negotiation event.
  { type: "Proposal" as const, id: "COLLABORATION" },
];

type CacheWriter = {
  dispatch: (action: unknown) => unknown;
  getState: () => unknown;
};

/**
 * Writes a mutation's answer into every cached grant list before the invalidation's refetch lands, so
 * the row that changed redraws from the server's answer at once instead of a moment later. The
 * refetch still runs, and replaces the whole list with the server's view.
 *
 * `replace` maps a cached row to its new state, or to `undefined` to keep it as it is.
 */
function writeIntoCachedLists(
  { dispatch, getState }: CacheWriter,
  replace: (row: PermissionJSON) => PermissionJSON | undefined,
): void {
  const state = getState() as Parameters<typeof permissionsApi.util.selectCachedArgsForQuery>[0];
  for (const args of permissionsApi.util.selectCachedArgsForQuery(state, "getPermissions")) {
    dispatch(
      permissionsApi.util.updateQueryData("getPermissions", args, (draft) => {
        draft.data = draft.data.map((row) => replace(row) ?? row);
      }),
    );
  }
}

/** The only filters a list may carry for a new grant to be written into it: who, what type, live or not. */
const filtersANewGrantCanBeJudgedBy = new Set(["granteeUserIds", "resourceTypes", "showDisabled", "legalEntityId"]);

/**
 * Whether a cached read is one person's grants, live, of every type or of the new grant's, on its
 * first page, and narrowed by nothing else: then the new grant belongs in it. A read narrowed further
 * — by record, by name, by removal date — is left to its refetch, because only the server can say
 * whether the new grant passes that narrowing.
 */
function holdsNewGrant(query: PermissionQuery, granted: PermissionJSON): boolean {
  const filters = query.filterParameters;
  if (!filters?.granteeUserIds?.some((id) => id.value === granted.granteeUserId)) return false;
  if ([...filters.searchParams.keys()].some((key) => !filtersANewGrantCanBeJudgedBy.has(key))) return false;
  if (filters.resourceTypes?.length && !filters.resourceTypes.includes(granted.resource.resourceType)) return false;
  if (filters.legalEntityId && filters.legalEntityId.value !== granted.entityId) return false;
  return (query.paginationParameters?.pageIndex ?? 0) === 0;
}

/**
 * Writes a grant `POST /permissions` just made into the person's cached lists before the refetch
 * lands, so the row appears at once (`docs/design/permissions/PERMISSIONS_UI_DESIGN.md`, "Showing
 * that it worked"). A row the list already holds — the server answers a grant the person already
 * had with that grant — is replaced; any other is put first, where the newest-first lists put it.
 * Only a grant no cached list held is counted into the person's per-type counts, so a repeated grant
 * is not counted twice.
 */
function writeNewGrantIntoCachedLists({ dispatch, getState }: CacheWriter, granted: PermissionJSON): void {
  if (granted.disabledDate) return;
  const state = getState() as Parameters<typeof permissionsApi.util.selectCachedArgsForQuery>[0];
  const lists = permissionsApi.util.selectCachedArgsForQuery(state, "getPermissions");
  const known = lists.some((args) =>
    permissionsApi.endpoints.getPermissions.select(args)(state).data?.data.some((row) => row.id === granted.id),
  );

  for (const args of lists) {
    if (!holdsNewGrant(args.query, granted)) continue;
    dispatch(
      permissionsApi.util.updateQueryData("getPermissions", args, (draft) => {
        const at = draft.data.findIndex((row) => row.id === granted.id);
        if (at >= 0) {
          draft.data[at] = granted;
          return;
        }
        draft.data.unshift(granted);
        draft.headers.totalElements += 1;
      }),
    );
  }
  if (known) return;

  const resourceType = granted.resource.resourceType;
  for (const args of permissionsApi.util.selectCachedArgsForQuery(state, "getPermissionsByGrantee")) {
    if (!holdsNewGrant(args.query, granted)) continue;
    dispatch(
      permissionsApi.util.updateQueryData("getPermissionsByGrantee", args, (draft) => {
        const person = draft.data.find((row) => row.granteeUserId === granted.granteeUserId);
        if (!person) return;
        person.permissionCounts[resourceType] = (person.permissionCounts[resourceType] ?? 0) + 1;
        person.totalPermissions += 1;
      }),
    );
  }
}

/** Each row the answer names, by id. */
function byId(rows: readonly PermissionJSON[]): Map<string | null | undefined, PermissionJSON> {
  return new Map(rows.map((row) => [row.id, row]));
}

/** A bound, not a limit anybody should meet: 50 pages of 100 grants. */
const maxPermissionPages = 50;

type PageResult = QueryReturnValue<unknown, FetchBaseQueryError, FetchBaseQueryMeta | undefined>;

type EveryPage = Readonly<{
  pages: unknown[];
  lastHeaders: ReturnType<typeof PaginatedResponse.parsePaginationHeader>;
  /** The bound stopped the reading while the server still had pages. */
  morePagesThanRead: boolean;
}>;

/** Every page of a grant list from the query's own page on, up to {@link maxPermissionPages}. */
async function readEveryPage(
  baseQuery: (args: FetchArgs) => PageResult | PromiseLike<PageResult>,
  query: PermissionQuery,
  description: string,
): Promise<EveryPage | { error: FetchBaseQueryError }> {
  const pages: unknown[] = [];
  let lastHeaders = PaginatedResponse.parsePaginationHeader("");
  const pageParams = new URLSearchParams(query.searchParams);
  for (let pageIndex = Number(pageParams.get("pageIndex") ?? 0); pageIndex < maxPermissionPages; pageIndex++) {
    pageParams.set("pageIndex", pageIndex.toString());
    const result = await baseQuery({ url: "permissions", params: new URLSearchParams(pageParams) });
    if (result.error) {
      return { error: result.error };
    }
    try {
      pages.push(...requireArray(result.data, description));
    } catch (error) {
      return { error: toCustomError(error) };
    }
    lastHeaders = PaginatedResponse.parsePaginationHeader(result.meta?.response?.headers.get("x-pagination") ?? "");
    const hasNext = lastHeaders.hasNext ?? lastHeaders.pageIndex < lastHeaders.totalPages - 1;
    if (!hasNext) break;
  }
  const morePagesThanRead = lastHeaders.hasNext ?? lastHeaders.pageIndex < lastHeaders.totalPages - 1;
  return { pages, lastHeaders, morePagesThanRead };
}

export const permissionsApi = api.injectEndpoints({
  endpoints: (build) => ({
    addPermission: build.mutation<PermissionJSON, PermissionMutationArgs>({
      query: ({ permission }) => ({
        url: "permissions",
        method: "POST",
        body: JSON.stringify(new PermissionCommand(permission)),
        headers: jsonPatchHeaders,
      }),
      transformResponse: (response: unknown) => PermissionSchema.validateJSON(response),
      async onQueryStarted(_args, api) {
        const granted = await api.queryFulfilled.then(({ data }) => data).catch(() => undefined);
        if (granted) writeNewGrantIntoCachedLists(api, granted);
      },
      invalidatesTags: getPermissionMutationTags,
    }),
    updatePermission: build.mutation<PermissionJSON, PermissionMutationArgs>({
      query: ({ permission }) => {
        if (!permission.id) {
          throw new Error("Permission ID is required to update a permission.");
        }

        return {
          url: `permissions/${permission.id.value}`,
          method: "PUT",
          body: JSON.stringify(new PermissionCommand(permission)),
          headers: jsonPatchHeaders,
        };
      },
      transformResponse: (response: unknown) => PermissionSchema.validateJSON(response),
      async onQueryStarted(_args, api) {
        const updated = await api.queryFulfilled.then(({ data }) => data).catch(() => undefined);
        if (updated) writeIntoCachedLists(api, (row) => (row.id === updated.id ? updated : undefined));
      },
      invalidatesTags: getPermissionMutationTags,
    }),
    bulkAddPermissions: build.mutation<BulkPermissionChangesJSON, BulkPermissionMutationArgs>({
      query: ({ permissions, replaceMatchingResourceTypes }) => ({
        url: "permissions/bulk",
        method: "POST",
        body: JSON.stringify(new BulkPermissionCommand(permissions, replaceMatchingResourceTypes)),
        headers: jsonPatchHeaders,
      }),
      transformResponse: (response: unknown) => BulkPermissionChangesJSONZ.parse(response),
      // Raised and disabled rows are rows the lists already hold; they redraw from the answer.
      async onQueryStarted(_args, api) {
        const changes = await api.queryFulfilled.then(({ data }) => data).catch(() => undefined);
        if (!changes) return;
        const changed = byId([...changes.raised, ...changes.disabled, ...changes.withdrawn]);
        writeIntoCachedLists(api, (row) => changed.get(row.id));
      },
      invalidatesTags: getPermissionMutationTags,
    }),
    /**
     * Applies a template to one person: the server grants as the caller, replaces the grants of the
     * kinds the template names, and records the application, so the grants it makes name the
     * template as their source (#6828).
     */
    applyPermissionTemplate: build.mutation<TemplateApplicationJSON, ApplyTemplateMutationArgs>({
      query: ({ templateId, templateVersion, granteeUserId, resources }) => ({
        url: `permissions/templates/${templateId.value}/applications`,
        method: "POST",
        body: JSON.stringify({
          granteeUserId: granteeUserId.value,
          resources: resources.map((resource) => JSON.stringify(resource.toJSON())),
          templateVersion,
        }),
        headers: jsonPatchHeaders,
      }),
      transformResponse: (response: unknown) => TemplateApplicationJSONZ.parse(response),
      // As for a bulk grant: raised and disabled rows are rows the lists already hold.
      async onQueryStarted(_args, api) {
        const changes = await api.queryFulfilled.then(({ data }) => data).catch(() => undefined);
        if (!changes) return;
        const changed = byId([...changes.raised, ...changes.disabled, ...changes.withdrawn]);
        writeIntoCachedLists(api, (row) => changed.get(row.id));
      },
      invalidatesTags: getPermissionMutationTags,
    }),
    getPermissions: build.query<Paginated<PermissionJSON>, PermissionQueryArgs>({
      serializeQueryArgs: ({ queryArgs }) => queryArgs.query.cacheKey,
      query: ({ query }) => {
        if (query.permissionView === PermissionView.Grantee) {
          throw new Error(
            "The getPermissions method is not supported when the 'Grantee' permissionView is specified in the permission query.",
          );
        }

        return {
          url: "permissions",
          params: query.searchParams,
        };
      },
      transformResponse: (response: unknown, meta): Paginated<PermissionJSON> => {
        const permissionJson = requireArray(response, "permissions response").map((permission) =>
          PermissionSchema.validateJSON(permission),
        );
        return {
          data: permissionJson,
          headers: PaginatedResponse.parsePaginationHeader(meta?.response?.headers.get("x-pagination") ?? ""),
        };
      },
      providesTags: (_result, _error, { query }) => [permissionListTag, ...permissionQueryTags(query.filterParameters)],
    }),
    /**
     * Every grant a list query matches, on every page, for a decision that one page could get wrong:
     * whether a person already holds a record (#6837). All or nothing: a list longer than the bound,
     * or a grant that will not parse, fails the read rather than answering part of it.
     */
    getEveryPermission: build.query<PermissionJSON[], PermissionQueryArgs>({
      serializeQueryArgs: ({ queryArgs }) => queryArgs.query.cacheKey,
      queryFn: async ({ query }, _api, _extraOptions, baseQuery) => {
        const read = await readEveryPage(baseQuery, query, "permissions response");
        if ("error" in read) return { error: read.error };
        if (read.morePagesThanRead) {
          return { error: toCustomError(`More than ${maxPermissionPages} pages of grants match; they cannot all be read.`) };
        }
        try {
          return { data: read.pages.map((permission) => PermissionSchema.validateJSON(permission)) };
        } catch (error) {
          return { error: toCustomError(error) };
        }
      },
      providesTags: [permissionListTag],
    }),
    /**
     * One grant's history, for the details pane. Tagged with the lists, so a removal or a re-grant
     * that adds a row to it reads it again.
     */
    getPermissionLineage: build.query<PermissionLineageJSON, string>({
      query: (permissionId) => `permissions/${permissionId}/lineage`,
      transformResponse: (response: unknown) => PermissionLineageJSONZ.parse(response),
      providesTags: [permissionListTag],
    }),
    getSessionPermissions: build.query<Paginated<PermissionJSON> | undefined, SessionPermissionQueryArgs>({
      serializeQueryArgs: ({ queryArgs }) => queryArgs.query?.cacheKey ?? "empty",
      queryFn: async ({ query }, _api, _extraOptions, baseQuery) => {
        if (!query) {
          return { data: PaginatedResponse.empty<PermissionJSON>() };
        }

        // Every page, not the first. The session is built from these grants, and a person who holds
        // more than one page of them -- a leader on many pieces of work holds a few per piece -- lost
        // every grant past the first page: those screens refused them, and nothing said why.
        const read = await readEveryPage(baseQuery, query, "session permissions response");
        if ("error" in read) return { error: read.error };
        const { pages, lastHeaders, morePagesThanRead } = read;

        try {
          // Per grant, not all-or-nothing. These are the permissions the whole session is built from:
          // the navigation is drawn from them and every guarded route waits on them, so rejecting the
          // response because one grant in it will not parse costs the user their entire application --
          // a stripped navigation and a page that loads forever. Dropping the unreadable grant denies
          // that one permission and leaves every other one working, which is both the safe direction
          // and a far smaller blast radius than the alternative.
          //
          // It is not silent: validateJSON has already logged the schema failure and the payload that
          // caused it, and the warning below names how many grants were lost.
          const permissionJson: PermissionJSON[] = [];
          let unreadable = 0;
          for (const permission of pages) {
            try {
              permissionJson.push(PermissionSchema.validateJSON(permission));
            } catch {
              unreadable += 1;
            }
          }
          if (unreadable > 0) {
            console.warn(
              `${unreadable} session permission(s) could not be read and have been treated as not granted. ` +
                "The schema failure above names the grant.",
            );
          }
          return {
            data: {
              data: permissionJson,
              // One page holding them all, unless the cap stopped the reading early: then the slice is
              // told more remain, and says so.
              headers: {
                ...lastHeaders,
                pageIndex: 0,
                pageSize: pages.length,
                totalPages: morePagesThanRead ? 2 : 1,
                hasPrev: false,
                hasNext: morePagesThanRead,
              },
            },
          };
        } catch (error) {
          return { error: toCustomError(error) };
        }
      },
      providesTags: [sessionPermissionTag],
    }),
    getPermissionsByGrantee: build.query<Paginated<PermissionsByGranteeJSON>, PermissionQueryArgs>({
      serializeQueryArgs: ({ queryArgs }) => queryArgs.query.cacheKey,
      query: ({ query }) => {
        if (query.permissionView !== PermissionView.Grantee) {
          throw new Error(
            "The getPermissionsByGrantee method is not supported if the 'Grantee' permissionView is NOT specified in the permission query.",
          );
        }

        return {
          url: "permissions",
          params: query.searchParams,
        };
      },
      transformResponse: (response: unknown, meta): Paginated<PermissionsByGranteeJSON> => {
        const permissionsByGranteeJson = requireArray(response, "permissions by grantee response").map((permission) =>
          PermissionsByGranteeSchema.validateJSON(permission),
        );
        return {
          data: permissionsByGranteeJson,
          headers: PaginatedResponse.parsePaginationHeader(meta?.response?.headers.get("x-pagination") ?? ""),
        };
      },
      providesTags: (_result, _error, { query }) => [permissionListTag, ...permissionQueryTags(query.filterParameters)],
    }),
    disablePermission: build.mutation<DisabledPermissionJSON, PermissionMutationArgs>({
      query: ({ permission }) => {
        if (!permission.id) {
          throw new Error("Permission ID is required to disable a permission.");
        }

        return {
          url: `permissions/${permission.id.value}`,
          method: "DELETE",
          params: { version: permission.version },
        };
      },
      transformResponse: (response: unknown) => DisabledPermissionJSONZ.parse(response),
      async onQueryStarted(_args, api) {
        const disabled = await api.queryFulfilled.then(({ data }) => data).catch(() => undefined);
        if (!disabled) return;
        const { withdrawnPermissions, ...permission } = disabled;
        const changed = byId([permission, ...withdrawnPermissions]);
        writeIntoCachedLists(api, (row) => changed.get(row.id));
      },
      invalidatesTags: getPermissionMutationTags,
    }),
    /**
     * Restores access the administrator revoked. The server re-grants rather than enables — it
     * mints a fresh permission and leaves the named one disabled, so the record of who revoked
     * access survives — which is why the route is `re-grant` and the response carries a different
     * id than the one sent. The list tags are invalidated, so the refetch picks the new grant up;
     * a caller that held on to the old id would re-grant it again and stack a row per call.
     */
    enablePermission: build.mutation<RegrantedPermissionJSON, PermissionMutationArgs>({
      query: ({ permission }) => {
        if (!permission.id) {
          throw new Error("Permission ID is required to enable a permission.");
        }

        return {
          url: `permissions/${permission.id.value}/re-grant`,
          method: "POST",
          params: { version: permission.version },
          body: {},
        };
      },
      transformResponse: (response: unknown) => RegrantedPermissionJSONZ.parse(response),
      // The cached row is the superseded one; it is swapped for the new grant — its id, version,
      // grantor and dates — so a second press re-grants nothing the server has not just answered for.
      // Each derived grant that came back with it replaces its withdrawn row the same way.
      async onQueryStarted(_args, api) {
        const regranted = await api.queryFulfilled.then(({ data }) => data).catch(() => undefined);
        if (!regranted) return;
        const { restoredPermissions, ...grant } = regranted;
        const replacements = new Map<string | null | undefined, PermissionJSON>();
        for (const { supersededPermissionId, ...permission } of [grant, ...(restoredPermissions ?? [])]) {
          replacements.set(supersededPermissionId ?? permission.id, permission);
        }
        writeIntoCachedLists(api, (row) => replacements.get(row.id));
      },
      invalidatesTags: getPermissionMutationTags,
    }),
    /**
     * "Remove all access…": disables every grant the person holds in the caller's organization but
     * notifications, and their own sign-in when asked, and lowers organization-wide notifications to
     * their own. The disabled, withdrawn and lowered rows redraw from the answer before the refetch
     * lands.
     */
    disableGranteePermissions: build.mutation<GranteeAccessRemovalJSON, DisableGranteePermissionsArgs>({
      query: ({ granteeUserId, stopSignIn }) => ({
        url: "permissions/disable-by-grantee",
        method: "POST",
        body: { granteeUserId: granteeUserId.value, stopSignIn },
      }),
      transformResponse: (response: unknown) => GranteeAccessRemovalJSONZ.parse(response),
      async onQueryStarted(_args, api) {
        const removal = await api.queryFulfilled.then(({ data }) => data).catch(() => undefined);
        if (!removal) return;
        const lowered = removal.loweredNotifications ? [removal.loweredNotifications.permission] : [];
        const changed = byId([...removal.disabled, ...removal.withdrawn, ...lowered]);
        writeIntoCachedLists(api, (row) => changed.get(row.id));
      },
      invalidatesTags: getPermissionMutationTags,
    }),
    /**
     * The undo of "Remove all access…": grants a set of removed grants again in one call, and raises
     * the notifications grant it lowered back in place. Each answer is a new grant naming the one it
     * superseded, which its cached row is swapped for, as one re-grant does; the raised notifications
     * row supersedes nothing and replaces itself.
     */
    regrantPermissions: build.mutation<RegrantedPermissionJSON[], RegrantPermissionsArgs>({
      query: ({ permissions, notifications }) => ({
        url: "permissions/re-grant",
        method: "POST",
        body: {
          permissions: permissions.map((permission) => ({ id: permission.id, version: permission.version })),
          ...(notifications && {
            notifications: {
              id: notifications.permission.id,
              version: notifications.permission.version,
              scope: notifications.previousScope,
              accessLevel: notifications.previousAccessLevel,
            },
          }),
        },
      }),
      transformResponse: (response: unknown) => z.array(RegrantedPermissionJSONZ).parse(response),
      async onQueryStarted(_args, api) {
        const regranted = await api.queryFulfilled.then(({ data }) => data).catch(() => undefined);
        if (!regranted) return;
        const replacements = new Map<string | null | undefined, PermissionJSON>();
        for (const { supersededPermissionId, ...permission } of regranted) {
          replacements.set(supersededPermissionId ?? permission.id, permission);
        }
        writeIntoCachedLists(api, (row) => replacements.get(row.id));
      },
      invalidatesTags: getPermissionMutationTags,
    }),
  }),
});

function requireArray(value: unknown, description: string): unknown[] {
  if (Array.isArray(value)) return value;
  throw new Error(`Expected ${description} to be an array.`);
}

function toCustomError(error: unknown): FetchBaseQueryError {
  return {
    status: "CUSTOM_ERROR",
    error: error instanceof Error ? error.message : String(error ?? "An error occurred."),
  };
}

export const {
  useAddPermissionMutation,
  useUpdatePermissionMutation,
  useBulkAddPermissionsMutation,
  useApplyPermissionTemplateMutation,
  useGetPermissionsQuery,
  useLazyGetPermissionsQuery,
  useGetEveryPermissionQuery,
  useGetPermissionLineageQuery,
  useGetSessionPermissionsQuery,
  useGetPermissionsByGranteeQuery,
  useDisablePermissionMutation,
  useEnablePermissionMutation,
  useDisableGranteePermissionsMutation,
  useRegrantPermissionsMutation,
} = permissionsApi;
